import React, { useMemo, useState } from 'react';
import { Navbar } from '../components/sections/Navbar';
import { SiteFooter } from '../components/sections/SiteFooter';
import { Button } from '../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Slider } from '../components/ui/Slider';
import { Switch } from '../components/ui/Switch';
import { Input } from '../components/ui/Input';
import { Label } from '../components/ui/Label';
import { Badge } from '../components/ui/Badge';
import { Textarea } from '../components/ui/Textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/Dialog';
import { ArrowRight, Calculator, Users, Wallet, CheckCircle2, Headset, Palette } from 'lucide-react';
export function TarifsPage() {
  const [isEur, setIsEur] = useState(false);
  const [participants, setParticipants] = useState<number>(250);
  const [isStartModalOpen, setIsStartModalOpen] = useState(false);
  const [isQuoteModalOpen, setIsQuoteModalOpen] = useState(false);
  // Constants
  const CONVERSION_RATE = 0.093;
  const MPF = 20000; // Frais minimum de plateforme en MAD
  // Format numbers with spaces for thousands
  const formatNumber = (num: number) => {
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  };
  // Convert to EUR if needed
  const displayAmount = (amountMAD: number) => {
    if (isEur) {
      return Math.round(amountMAD * CONVERSION_RATE);
    }
    return amountMAD;
  };
  const currencyLabel = isEur ? 'EUR' : 'MAD';
  // Calculate pricing
  const {
    totalMAD,
    perHeadMAD
  } = useMemo(() => {
    let usageFee = 0;
    const n = participants || 1; // Prevent division by zero
    if (n <= 100) {
      usageFee = n * 150;
    } else if (n <= 500) {
      usageFee = 100 * 150 + (n - 100) * 100;
    } else {
      usageFee = 100 * 150 + 400 * 100 + (n - 500) * 70;
    }
    const total = MPF + usageFee;
    const perHead = Math.round(total / n);
    return {
      totalMAD: total,
      perHeadMAD: perHead
    };
  }, [participants]);
  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = parseInt(e.target.value, 10);
    if (isNaN(val)) {
      setParticipants(1);
    } else if (val > 2000) {
      setParticipants(2000);
    } else if (val < 1) {
      setParticipants(1);
    } else {
      setParticipants(val);
    }
  };
  // Pre-calculated examples
  const examples = [{
    count: 150,
    total: 40000,
    perHead: 266
  }, {
    count: 250,
    total: 50000,
    perHead: 200
  }, {
    count: 600,
    total: 82000,
    perHead: 136
  }];
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Handle form submission logic here
    setIsStartModalOpen(false);
    setIsQuoteModalOpen(false);
  };
  return <div className="min-h-screen bg-[#09090B] font-heading text-white selection:bg-cyan-500/30 flex flex-col" data-id="element-826">
      <Navbar data-id="element-827" />

      <main className="flex-1 relative overflow-hidden" data-id="element-828">
        {/* Background Elements - Enhanced dynamic gradient */}
        <div className="absolute inset-0 opacity-40 pointer-events-none" style={{
        backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.03) 1px, transparent 1px)',
        backgroundSize: '24px 24px'
      }} data-id="element-829" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-gradient-to-b from-cyan-500/10 to-blue-600/5 rounded-full blur-[150px] pointer-events-none" data-id="element-830" />
        <div className="absolute bottom-0 right-0 w-[600px] h-[600px] bg-blue-600/[0.05] rounded-full blur-[120px] pointer-events-none" data-id="element-831" />

        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28" data-id="element-832">
          {/* SECTION 1 — EN-TÊTE DE PAGE */}
          <section className="text-center mb-24" data-id="element-833">
            <Badge className="mb-6 bg-cyan-500/10 text-cyan-400 border-cyan-500/20 px-4 py-1.5 text-xs font-mono uppercase tracking-wider rounded-full" data-id="element-834">
              Tarification
            </Badge>
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold leading-[1.1] text-white tracking-tight mb-6" data-id="element-835">
              Tarification transparente
            </h1>
            <p className="text-xl md:text-2xl font-semibold text-cyan-400 max-w-3xl mx-auto mb-12" data-id="element-836">
              Aucun abonnement. Aucune surprise. Vous payez pour ce que vous
              utilisez — et rien d'autre.
            </p>

            <div className="flex items-center justify-center gap-4 bg-white/[0.03] border border-white/[0.08] rounded-full py-3 px-6 w-fit mx-auto backdrop-blur-sm shadow-[0_0_20px_rgba(255,255,255,0.02)]" data-id="element-837">
              <Label htmlFor="currency-toggle" className={`text-sm font-medium cursor-pointer transition-colors ${!isEur ? 'text-white' : 'text-white/40'}`} data-id="element-838">
                MAD
              </Label>
              <Switch id="currency-toggle" checked={isEur} onCheckedChange={setIsEur} className="data-[state=checked]:bg-blue-500 data-[state=unchecked]:bg-cyan-500" data-id="element-839" />
              <Label htmlFor="currency-toggle" className={`text-sm font-medium cursor-pointer transition-colors ${isEur ? 'text-white' : 'text-white/40'}`} data-id="element-840">
                EUR
              </Label>
            </div>
          </section>

          {/* SECTION 2 — STRUCTURE TARIFAIRE */}
          <section className="mb-24" data-id="element-841">
            <h2 className="text-2xl md:text-3xl font-bold text-center mb-10" data-id="element-842">
              Comment ça fonctionne
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-843">
              {/* CARTE 1 — Frais de plateforme fixes */}
              <Card className="bg-[#09090B]/90 border-white/[0.08] backdrop-blur-sm relative overflow-hidden group transition-all duration-300 hover:border-cyan-500/30 hover:shadow-[0_0_30px_rgba(6,182,212,0.1)]" data-id="element-844">
                <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" data-id="element-845" />
                <CardHeader className="relative z-10" data-id="element-846">
                  <div className="w-12 h-12 rounded-xl bg-cyan-500/10 flex items-center justify-center mb-4 border border-cyan-500/20 group-hover:scale-110 transition-transform duration-300" data-id="element-847">
                    <Wallet className="w-6 h-6 text-cyan-400" data-id="element-848" />
                  </div>
                  <CardTitle className="text-xl text-white" data-id="element-849">
                    Accès à la plateforme
                  </CardTitle>
                </CardHeader>
                <CardContent className="relative z-10" data-id="element-850">
                  <div className="mb-4" data-id="element-851">
                    <span className="text-4xl font-bold text-white" data-id="element-852">
                      {formatNumber(displayAmount(MPF))}
                    </span>
                    <span className="text-xl text-white/50 ml-2" data-id="element-853">
                      {currencyLabel} TTC
                    </span>
                  </div>
                  <p className="text-white leading-relaxed font-medium" data-id="element-854">
                    Forfait fixe par événement. Ce montant ne change pas.
                  </p>
                  <p className="text-white/80 leading-relaxed mt-2 text-sm" data-id="element-855">
                    Inclus dans chaque déploiement. Couvre la configuration, la
                    personnalisation et l'accès à l'ensemble des
                    fonctionnalités.
                  </p>
                </CardContent>
              </Card>

              {/* CARTE 2 — Frais d'utilisation dégressive */}
              <Card className="bg-[#09090B]/90 border-white/[0.08] backdrop-blur-sm relative overflow-hidden group transition-all duration-300 hover:border-blue-500/30 hover:shadow-[0_0_30px_rgba(59,130,246,0.1)]" data-id="element-856">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" data-id="element-857" />
                <CardHeader className="relative z-10" data-id="element-858">
                  <div className="w-12 h-12 rounded-xl bg-blue-500/10 flex items-center justify-center mb-4 border border-blue-500/20 group-hover:scale-110 transition-transform duration-300" data-id="element-859">
                    <Users className="w-6 h-6 text-blue-400" data-id="element-860" />
                  </div>
                  <CardTitle className="text-xl text-white" data-id="element-861">
                    Frais par participant
                  </CardTitle>
                </CardHeader>
                <CardContent className="relative z-10" data-id="element-862">
                  <div className="space-y-3 mb-6" data-id="element-863">
                    <div className="flex justify-between items-center py-2 border-b border-white/[0.06]" data-id="element-864">
                      <span className="text-white/80" data-id="element-865">
                        1 – 100 participants
                      </span>
                      <span className="font-semibold text-white" data-id="element-866">
                        {formatNumber(displayAmount(150))} {currencyLabel}{' '}
                        TTC/pers.
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-white/[0.06]" data-id="element-867">
                      <span className="text-white/80" data-id="element-868">
                        101 – 500 participants
                      </span>
                      <span className="font-semibold text-white" data-id="element-869">
                        {formatNumber(displayAmount(100))} {currencyLabel}{' '}
                        TTC/pers.
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-white/[0.06]" data-id="element-870">
                      <span className="text-white/80" data-id="element-871">501+ participants</span>
                      <span className="font-semibold text-white" data-id="element-872">
                        {formatNumber(displayAmount(70))} {currencyLabel}{' '}
                        TTC/pers.
                      </span>
                    </div>
                  </div>
                  <p className="text-sm text-white/70 italic" data-id="element-873">
                    * La dégressivité s'applique par tranche, pas sur la
                    totalité.
                  </p>
                </CardContent>
              </Card>
            </div>
          </section>

          {/* SECTION 3 — NOUVELLE SECTION "Ce que vous obtenez" */}
          <section className="mb-24" data-id="element-874">
            <h2 className="text-2xl md:text-3xl font-bold text-center mb-12" data-id="element-875">
              Tout inclus. Dès le premier jour.
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-876">
              <div className="text-center" data-id="element-877">
                <div className="w-12 h-12 mx-auto rounded-full bg-cyan-500/10 flex items-center justify-center mb-4 border border-cyan-500/20" data-id="element-878">
                  <CheckCircle2 className="w-6 h-6 text-cyan-400" data-id="element-879" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2" data-id="element-880">
                  Toutes les fonctionnalités disponibles
                </h3>
                <p className="text-white/80 text-sm" data-id="element-881">
                  Aucun module payant, aucune restriction
                </p>
              </div>
              <div className="text-center" data-id="element-882">
                <div className="w-12 h-12 mx-auto rounded-full bg-blue-500/10 flex items-center justify-center mb-4 border border-blue-500/20" data-id="element-883">
                  <Headset className="w-6 h-6 text-blue-400" data-id="element-884" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2" data-id="element-885">
                  Support à l'onboarding
                </h3>
                <p className="text-white/80 text-sm" data-id="element-886">
                  Accompagnement à la configuration de votre événement
                </p>
              </div>
              <div className="text-center" data-id="element-887">
                <div className="w-12 h-12 mx-auto rounded-full bg-indigo-500/10 flex items-center justify-center mb-4 border border-indigo-500/20" data-id="element-888">
                  <Palette className="w-6 h-6 text-indigo-400" data-id="element-889" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2" data-id="element-890">
                  White-label complet
                </h3>
                <p className="text-white/80 text-sm" data-id="element-891">
                  Votre logo, votre URL, vos couleurs — MeetOS est invisible
                </p>
              </div>
            </div>
          </section>

          {/* SECTION 4 — CALCULATEUR INTERACTIF */}
          <section className="mb-24" data-id="element-892">
            <Card className="bg-white/[0.02] border-white/[0.08] backdrop-blur-md overflow-hidden shadow-[0_0_50px_rgba(6,182,212,0.05)]" data-id="element-893">
              <div className="p-8 md:p-10 border-b border-white/[0.08] bg-white/[0.01]" data-id="element-894">
                <div className="flex items-center gap-3 mb-4" data-id="element-895">
                  <Calculator className="w-6 h-6 text-cyan-400" data-id="element-896" />
                  <h2 className="text-2xl md:text-3xl font-bold text-white" data-id="element-897">
                    Estimez le coût de votre événement
                  </h2>
                </div>
                <p className="text-white/80" data-id="element-898">
                  Déplacez le curseur ou entrez directement le nombre de
                  participants.
                </p>
              </div>

              <CardContent className="p-8 md:p-10" data-id="element-899">
                {/* Slider & Input Controls */}
                <div className="flex flex-col md:flex-row items-center gap-8 mb-12" data-id="element-900">
                  <div className="flex-1 w-full" data-id="element-901">
                    <div className="flex justify-between mb-4 text-sm text-white/50 font-mono" data-id="element-902">
                      <span data-id="element-903">1</span>
                      <span data-id="element-904">2000+</span>
                    </div>
                    <Slider value={[participants]} min={1} max={2000} step={1} onValueChange={val => setParticipants(val[0])} className="cursor-pointer" data-id="element-905" />
                  </div>
                  <div className="w-full md:w-48 flex-shrink-0" data-id="element-906">
                    <Label htmlFor="participants-input" className="sr-only" data-id="element-907">
                      Nombre de participants
                    </Label>
                    <div className="relative" data-id="element-908">
                      <Input id="participants-input" type="number" min={1} max={2000} value={participants} onChange={handleInputChange} className="bg-white/[0.05] border-white/[0.1] text-white text-xl text-center h-14 pr-12 focus-visible:ring-cyan-500/50" data-id="element-909" />
                      <div className="absolute right-4 top-1/2 -translate-y-1/2 text-white/30" data-id="element-910">
                        <Users className="w-5 h-5" data-id="element-911" />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Results Display */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-12" data-id="element-912">
                  <div className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-6 text-center" data-id="element-913">
                    <p className="text-white/50 text-sm mb-2 uppercase tracking-wider font-mono" data-id="element-914">
                      Participants
                    </p>
                    <p className="text-3xl font-bold text-white" data-id="element-915">
                      {formatNumber(participants)}
                    </p>
                  </div>
                  <div className="bg-gradient-to-r from-cyan-500 to-blue-600 rounded-xl p-6 text-center shadow-[0_0_40px_rgba(6,182,212,0.3)] transform scale-105 z-10" data-id="element-916">
                    <p className="text-white/90 text-sm mb-2 uppercase tracking-wider font-mono font-semibold" data-id="element-917">
                      Devis total
                    </p>
                    <p className="text-4xl font-bold text-white" data-id="element-918">
                      {formatNumber(displayAmount(totalMAD))}{' '}
                      <span className="text-2xl text-white/80" data-id="element-919">
                        {currencyLabel} TTC
                      </span>
                    </p>
                  </div>
                  <div className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-6 text-center" data-id="element-920">
                    <p className="text-white/50 text-sm mb-2 uppercase tracking-wider font-mono" data-id="element-921">
                      Prix par personne
                    </p>
                    <p className="text-3xl font-bold text-white" data-id="element-922">
                      {formatNumber(displayAmount(perHeadMAD))}{' '}
                      <span className="text-xl text-white/60" data-id="element-923">
                        {currencyLabel} TTC/pers.
                      </span>
                    </p>
                  </div>
                </div>

                {/* CTA */}
                <div className="text-center mb-16" data-id="element-924">
                  <Button size="lg" onClick={() => setIsStartModalOpen(true)} className="bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white border-0 px-10 py-6 text-lg rounded-full shadow-[0_0_30px_rgba(6,182,212,0.4)] hover:shadow-[0_0_40px_rgba(6,182,212,0.6)] transition-all" data-id="element-925">
                    Je veux ce tarif
                    <ArrowRight className="ml-2 w-5 h-5" data-id="element-926" />
                  </Button>
                  <p className="mt-4 text-white/80 text-sm" data-id="element-927">
                    Votre plateforme sera en ligne en moins de 48h.
                  </p>
                </div>

                {/* Pre-calculated Examples */}
                <div data-id="element-928">
                  <p className="text-sm text-white/60 mb-6 text-center font-semibold uppercase tracking-wider" data-id="element-929">
                    Quelques exemples concrets :
                  </p>
                  <div className="flex flex-wrap justify-center gap-3" data-id="element-930">
                    {examples.map((ex, idx) => <button key={idx} onClick={() => setParticipants(ex.count)} className="bg-white/[0.03] hover:bg-white/[0.08] border border-white/[0.06] hover:border-cyan-500/30 text-white/70 hover:text-white text-sm py-2 px-4 rounded-full transition-all duration-200 flex items-center gap-2" data-id="element-931">
                        <span className="font-semibold text-cyan-400" data-id="element-932">
                          {ex.count} pax
                        </span>
                        <span className="text-white/30" data-id="element-933">→</span>
                        <span data-id="element-934">
                          {formatNumber(displayAmount(ex.total))}{' '}
                          {currencyLabel} TTC
                        </span>
                        <span className="text-white/30" data-id="element-935">—</span>
                        <span data-id="element-936">
                          {formatNumber(displayAmount(ex.perHead))}{' '}
                          {currencyLabel} TTC/pers.
                        </span>
                      </button>)}
                  </div>
                </div>
              </CardContent>
            </Card>
          </section>

          {/* SECTION 5 — NOTE DE BAS DE PAGE */}
          <section className="text-center max-w-3xl mx-auto" data-id="element-937">
            <div className="bg-white/[0.02] border border-white/[0.05] rounded-2xl p-8 backdrop-blur-sm" data-id="element-938">
              <p className="text-white/90 leading-relaxed mb-8" data-id="element-939">
                Les tarifs sont exprimés TTC. Un devis personnalisé peut être
                établi pour les événements récurrents ou les déploiements
                multi-événements. Contactez-nous pour en savoir plus.
              </p>
              <Button size="lg" onClick={() => setIsQuoteModalOpen(true)} className="bg-white text-black hover:bg-white/90 px-8 rounded-full font-medium" data-id="element-940">
                Obtenir mon devis gratuit
                <ArrowRight className="ml-2 w-4 h-4" data-id="element-941" />
              </Button>
            </div>
          </section>
        </div>
      </main>

      <SiteFooter data-id="element-942" />

      {/* MODAL 1 - Démarrer mon événement */}
      <Dialog open={isStartModalOpen} onOpenChange={setIsStartModalOpen} data-id="element-943">
        <DialogContent className="bg-[#09090B] border-cyan-500/30 text-white max-w-md w-full sm:rounded-2xl" data-id="element-944">
          <DialogHeader data-id="element-945">
            <DialogTitle className="text-2xl font-bold" data-id="element-946">
              Réservez votre plateforme
            </DialogTitle>
            <DialogDescription className="text-white/60" data-id="element-947">
              Nos équipes vous contactent sous 24h avec une proposition sur
              mesure.
            </DialogDescription>
          </DialogHeader>

          <div className="bg-white/[0.03] border border-white/[0.08] rounded-xl p-4 mb-6" data-id="element-948">
            <div className="flex justify-between items-center py-2 border-b border-white/[0.06]" data-id="element-949">
              <span className="text-white/60 text-sm" data-id="element-950">
                Participants estimés
              </span>
              <span className="font-semibold" data-id="element-951">
                {formatNumber(participants)}
              </span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-white/[0.06]" data-id="element-952">
              <span className="text-white/60 text-sm" data-id="element-953">Devis estimatif</span>
              <span className="font-semibold text-cyan-400" data-id="element-954">
                {formatNumber(displayAmount(totalMAD))} {currencyLabel} TTC
              </span>
            </div>
            <div className="flex justify-between items-center py-2" data-id="element-955">
              <span className="text-white/60 text-sm" data-id="element-956">Prix par personne</span>
              <span className="font-semibold" data-id="element-957">
                {formatNumber(displayAmount(perHeadMAD))} {currencyLabel}/pers.
              </span>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4" data-id="element-958">
            <div className="space-y-2" data-id="element-959">
              <Label htmlFor="start-name" data-id="element-960">Prénom & Nom *</Label>
              <Input id="start-name" required className="bg-white/[0.05] border-white/[0.1] text-white" data-id="element-961" />
            </div>
            <div className="space-y-2" data-id="element-962">
              <Label htmlFor="start-org" data-id="element-963">Organisation *</Label>
              <Input id="start-org" required className="bg-white/[0.05] border-white/[0.1] text-white" data-id="element-964" />
            </div>
            <div className="space-y-2" data-id="element-965">
              <Label htmlFor="start-email" data-id="element-966">Email professionnel *</Label>
              <Input id="start-email" type="email" required className="bg-white/[0.05] border-white/[0.1] text-white" data-id="element-967" />
            </div>
            <div className="space-y-2" data-id="element-968">
              <Label htmlFor="start-phone" data-id="element-969">Téléphone</Label>
              <Input id="start-phone" type="tel" className="bg-white/[0.05] border-white/[0.1] text-white" data-id="element-970" />
            </div>
            <div className="space-y-2" data-id="element-971">
              <Label htmlFor="start-message" data-id="element-972">
                Message / contexte de l'événement
              </Label>
              <Textarea id="start-message" className="bg-white/[0.05] border-white/[0.1] text-white resize-none" rows={3} data-id="element-973" />
            </div>
            <Button type="submit" className="w-full bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white border-0 mt-2" data-id="element-974">
              Envoyer ma demande →
            </Button>
            <p className="text-xs text-center text-white/40 mt-4" data-id="element-975">
              En soumettant ce formulaire, vous acceptez d'être contacté par
              l'équipe MeetOS.
            </p>
          </form>
        </DialogContent>
      </Dialog>

      {/* MODAL 2 - Demander un devis */}
      <Dialog open={isQuoteModalOpen} onOpenChange={setIsQuoteModalOpen} data-id="element-976">
        <DialogContent className="bg-[#09090B] border-cyan-500/30 text-white max-w-md w-full sm:rounded-2xl" data-id="element-977">
          <DialogHeader data-id="element-978">
            <DialogTitle className="text-2xl font-bold" data-id="element-979">
              Parlons de votre événement
            </DialogTitle>
            <DialogDescription className="text-white/60" data-id="element-980">
              Dites-nous en plus. On revient vers vous rapidement.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4 mt-4" data-id="element-981">
            <div className="space-y-2" data-id="element-982">
              <Label htmlFor="quote-name" data-id="element-983">Prénom & Nom *</Label>
              <Input id="quote-name" required className="bg-white/[0.05] border-white/[0.1] text-white" data-id="element-984" />
            </div>
            <div className="space-y-2" data-id="element-985">
              <Label htmlFor="quote-org" data-id="element-986">Organisation *</Label>
              <Input id="quote-org" required className="bg-white/[0.05] border-white/[0.1] text-white" data-id="element-987" />
            </div>
            <div className="space-y-2" data-id="element-988">
              <Label htmlFor="quote-email" data-id="element-989">Email professionnel *</Label>
              <Input id="quote-email" type="email" required className="bg-white/[0.05] border-white/[0.1] text-white" data-id="element-990" />
            </div>
            <div className="space-y-2" data-id="element-991">
              <Label htmlFor="quote-phone" data-id="element-992">Téléphone</Label>
              <Input id="quote-phone" type="tel" className="bg-white/[0.05] border-white/[0.1] text-white" data-id="element-993" />
            </div>
            <div className="space-y-2" data-id="element-994">
              <Label htmlFor="quote-participants" data-id="element-995">
                Nombre estimé de participants
              </Label>
              <Input id="quote-participants" type="number" className="bg-white/[0.05] border-white/[0.1] text-white" data-id="element-996" />
            </div>
            <div className="space-y-2" data-id="element-997">
              <Label htmlFor="quote-message" data-id="element-998">
                Message / description du projet
              </Label>
              <Textarea id="quote-message" className="bg-white/[0.05] border-white/[0.1] text-white resize-none" rows={3} data-id="element-999" />
            </div>
            <Button type="submit" className="w-full bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white border-0 mt-2" data-id="element-1000">
              Envoyer ma demande →
            </Button>
            <p className="text-xs text-center text-white/40 mt-4" data-id="element-1001">
              En soumettant ce formulaire, vous acceptez d'être contacté par
              l'équipe MeetOS.
            </p>
          </form>
        </DialogContent>
      </Dialog>
    </div>;
}